import { asInt } from '@/utils';
import { createRouter, createWebHistory, type Router } from 'vue-router';

import { useAuthStore } from '@/stores/authentication';
import config from '@/config';

export interface Routed {
    router?: Router;
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            component: () => import('../views/page.vue'),
            name: 'home',
            children: [
                {
                    path: '',
                    component: () => import('../views/news/news.vue'),
                    name: 'news',
                    meta: { title: 'news' },
                    props: route => ({
                        forWhom: 'ROLE_PERFORMER',
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 5
                    })
                },
                {
                    path: '/profile/',
                    component: () => import('../views/profile/profile.vue'),
                    name: 'profile',
                    children: [
                        {
                            path: 'info/',
                            component: () => import('../views/profile/info/info.vue'),
                            name: 'profile-info',
                            meta: { title: 'profile-info' },
                        },
                        {
                            path: 'desc/',
                            component: () => import('../views/profile/desc/desc.vue'),
                            name: 'profile-desc',
                            meta: { title: 'profile-desc' },
                        },
                        {
                            path: 'photos/',
                            component: () => import('../views/profile/photos/photos.vue'),
                            name: 'profile-photos',
                            meta: { title: 'profile-photos' }
                        },
                        {
                            path: 'videos/',
                            component: () => import('../views/profile/videos/videos.vue'),
                            name: 'profile-videos',
                            meta: { title: 'profile-videos' }
                        },
                        {
                            path: 'location/',
                            component: () => import('../views/profile/location/location.vue'),
                            name: 'profile-location',
                            meta: { title: 'profile-location' },
                        },
                        {
                            path: 'phone/',
                            component: () => import('../views/profile/phone/phone.vue'),
                            name: 'profile-phone',
                            meta: { title: 'profile-phone' }
                        },
                    ],
                },
                {
                    path: '/earnings/',
                    component: () => import('../views/earnings/earnings.vue'),
                    name: 'earnings',
                    meta: { title: 'earnings' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20,
                        date: Date.now(),
                        scope: 'day',
                        service: 'ALL'
                    })
                },
                {
                    path: '/earnings/specifications/',
                    component: () => import('../views/earnings/specs/specs.vue'),
                    name: 'specifications',
                    meta: { title: 'earnings' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/earnings/targets/',
                    component: () => import('../views/earnings/targets/targets.vue'),
                    name: 'targets',
                    meta: { title: 'targets' }
                },
                {
                    path: '/settings/',
                    component: () => import('../views/profile/settings/settings.vue'),
                    name: 'settings',
                    meta: { title: 'settings' }
                },
                {
                    path: '/personal/',
                    component: () => import('../views/profile/personal/personal.vue'),
                    name: 'personal',
                    meta: { title: 'personal' }
                },
                {
                    path: '/brands/',
                    component: () => import('../views/profile/brands/brands.vue'),
                    name: 'brands',
                    meta: { title: 'brands' }
                },
                {
                    path: '/vip/',
                    component: () => import('../views/profile/vip/vip.vue'),
                    name: 'vip',
                    meta: { title: 'vip' }
                },
                {
                    path: '/profile/documents/',
                    component: () => import('../views/profile/documents/documents.vue'),
                    name: 'documents',
                    meta: { title: 'documents' }
                },
                {
                    path: '/memos/',
                    component: () => import('../views/profile/memos/memos.vue'),
                    name: 'memos',
                    meta: { title: 'memos' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/inbox/:inboxType?/',
                    component: () => import('../views/inbox/inbox.vue'),
                    name: 'inbox',
                    meta: { title: 'inbox' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 10
                    })
                },
                {
                    path: '/forum/',
                    component: () => import('../views/forum/forum.vue'),
                    name: 'forum',
                    meta: { title: 'forum' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/forum/thread/:id/new/',
                    component: () => import('../views/forum/thread/new-thread.vue'),
                    name: 'newThread',
                    meta: { title: 'forum' }
                },
                {
                    path: '/forum/thread/',
                    component: () => import('../views/forum/thread/thread.vue'),
                    name: 'threadSearch',
                    meta: { title: 'forum' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/forum/thread/:id/',
                    component: () => import('../views/forum/thread/thread.vue'),
                    name: 'thread',
                    meta: { title: 'forum' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/forum/topic/:id/',
                    component: () => import('../views/forum/topic/topic.vue'),
                    name: 'topic',
                    meta: { title: 'forum' },
                    props: route => ({
                        id: asInt(route.query.id),
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20
                    })
                },
                {
                    path: '/faq/',
                    component: () => import('../views/faq/faq.vue'),
                    name: 'faq',
                    meta: { title: 'faq' }
                },
                {
                    path: '/clients/',
                    component: () => import('../views/clients/clients.vue'),
                    name: 'clients',
                    meta: { title: 'clients' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 20,
                        order: 'last_contact',
                        search: undefined,
                        startDate: undefined
                    })
                },
                {
                    path: '/clients/:client_id/',
                    component: () => import('../views/clients/client/client.vue'),
                    name: 'client',
                    meta: { title: 'client' },
                    props: route => ({
                        page: asInt(route.query.page) || 1,
                        offset: asInt(route.query.offset) || 0,
                        limit: asInt(route.query.limit) || 5,
                        username: undefined,
                        country: undefined
                    })
                },
                {
                    path: '/toys/',
                    component: () => import('../views/toys/toys.vue'),
                    name: 'toys',
                    meta: { title: 'toys' }
                },
                {
                    path: '/privacy-policy/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'policy',
                    meta: { title: 'policy' }
                },
                {
                    path: '/cookies/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'cookies',
                    meta: { title: 'cookies' }
                },
                {
                    path: '/terms/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'terms',
                    meta: { title: 'terms' }
                },
                {
                    path: '/complaints/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'complaints',
                    meta: { title: 'complaints' }
                },
                {
                    path: '/instructions/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'instructions',
                    meta: { title: 'instructions' }
                },
                {
                    path: '/contact/',
                    component: () => import('../views/textpages/textpages.vue'),
                    name: 'contact',
                    meta: { title: 'contact' }
                }
            ]
        },
        {
            path: '/register/:token',
            component: () => import('../views/register/register.vue'),
            name: 'register'
        },
        {
            path: '/login/',
            component: () => import('../views/login/login.vue'),
            name: 'login',
            beforeEnter: localizeInterceptor
        },
        {
            path: '/forgot-password/',
            component: () => import('../views/login/forgot-password.vue'),
            name: 'forgot-password',
            beforeEnter: localizeInterceptor
        },
        {
            path: '/reset-password/:performerId/:token/',
            component: () => import('../views/login/reset-password.vue'),
            name: 'reset-password',
            beforeEnter: localizeInterceptor
        },
        {
            path: '/reset',
            component: () => import('../views/reset-cookies/reset-cookies.vue'),
            name: 'reset'
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    const performer = useAuthStore();
    if (!performer.isLoggedIn() && to.name != 'login' && to.name != 'register' && to.name != 'reset' && to.name != 'forgot-password' && to.name != 'reset-password') {
        await performer.authenticate();
    }

    //Don't allow access to the login page if the performer is already logged in
    if (to.name === 'login' && performer.isLoggedIn()) {
        next({
            name: 'news'
        });

        return;
    }

    // Don't allow access to the forum if performer is blocked OR if the theme is 'dusk'
    if (
        (to.name === 'newThread' || to.name === 'thread' || to.name === 'topic' || to.name === 'forum') &&
        (!performer.getForumBlocked || config.Themename === 'dusk')
    ) {
        next({ name: 'news' }); // Redirect to the news page instead of forum
        return;
    }

    //If the user goes to any other page besides login, and is not logged in, redirect back to login page
    const validRoute = to.name === 'login' || to.name === 'register' || to.name === 'reset' || to.name === 'forgot-password' || to.name === 'reset-password' ? false : true;
    if (!performer.isLoggedIn() && validRoute) {
        next({
            name: 'login'
        });

        return;
    }

    next();
});

export function localizeInterceptor(to: any, from: any, next: (location?: any) => void){
    const performer = useAuthStore();
    performer.setLocalize({annon: 1});
    next();
}

export default router;
